import { useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import OfferCard from "@/components/base/OfferCard";
import { useAdminMarketplaces } from "@/hooks/useAdminMarketplaces";
import { useAdminProducts } from "@/hooks/useAdminStore";
import AdminHeader from "@/pages/admin/components/AdminHeader";

type PlacementFlag = "showInDailyDeals" | "showInFeatured";
type StatusFilter = "todos" | "habilitados" | "desabilitados";
type LayoutMode = "table" | "cards";

const MARKETPLACE_COLORS: Record<string, string> = {
  Amazon: "bg-amber-100 text-amber-700",
  "Mercado Livre": "bg-yellow-100 text-yellow-700",
  Shopee: "bg-orange-100 text-orange-700",
  "Magazine Luiza": "bg-blue-100 text-blue-700",
  Americanas: "bg-red-100 text-red-700",
  Kabum: "bg-purple-100 text-purple-700",
};

interface Props {
  title: string;
  subtitle: string;
  flag: PlacementFlag;
  icon: string;
  enabledLabel: string;
  enabledDescription: string;
  emptyLabel: string;
  layout?: LayoutMode;
  topContent?: ReactNode;
}

export default function ProductPlacementManager({
  title,
  subtitle,
  flag,
  icon,
  enabledLabel,
  enabledDescription,
  emptyLabel,
  layout = "table",
  topContent,
}: Props) {
  const navigate = useNavigate();
  const { products, updateProduct } = useAdminProducts();
  const { activeMarketplaceOptions } = useAdminMarketplaces();
  const [search, setSearch] = useState("");
  const [filterMarketplace, setFilterMarketplace] = useState("Todos");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("todos");
  const [toast, setToast] = useState<string | null>(null);

  const enabledCount = useMemo(
    () => products.filter((product) => product[flag]).length,
    [flag, products]
  );

  const marketplaces = useMemo(
    () => ["Todos", ...Array.from(new Set([...activeMarketplaceOptions, ...products.map((product) => product.marketplace)].filter(Boolean)))],
    [activeMarketplaceOptions, products]
  );

  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      const matchSearch = product.name.toLowerCase().includes(search.toLowerCase());
      const matchMarketplace = filterMarketplace === "Todos" || product.marketplace === filterMarketplace;
      const matchStatus =
        statusFilter === "todos" ||
        (statusFilter === "habilitados" && product[flag]) ||
        (statusFilter === "desabilitados" && !product[flag]);

      return matchSearch && matchMarketplace && matchStatus;
    });
  }, [filterMarketplace, flag, products, search, statusFilter]);

  const showToast = (message: string) => {
    setToast(message);
    setTimeout(() => setToast(null), 3000);
  };

  const togglePlacement = (productId: number, enabled: boolean) => {
    updateProduct(productId, { [flag]: enabled } as Pick<typeof products[number], PlacementFlag>);
    showToast(enabled ? `${enabledLabel} habilitado.` : `${enabledLabel} desabilitado.`);
  };

  return (
    <div>
      <AdminHeader
        title={title}
        subtitle={`${enabledCount} habilitado(s) · ${products.length} produto(s) no cadastro`}
        action={
          <button
            onClick={() => navigate("/admin/produtos")}
            className="px-4 py-2.5 rounded-xl bg-orange-500 text-white text-sm font-bold hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-shopping-bag-3-line mr-1.5"></i>Ir para Produtos
          </button>
        }
      />

      {topContent}

      <div className="mb-6 rounded-2xl border border-orange-100 bg-orange-50 px-5 py-4 flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-white text-orange-500 border border-orange-100 flex items-center justify-center flex-shrink-0">
          <i className={`${icon} text-lg`}></i>
        </div>
        <div>
          <p className="text-sm font-semibold text-orange-700">{subtitle}</p>
          <p className="text-sm text-orange-600 mt-0.5">{enabledDescription}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-48">
          <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar produto..."
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
          />
        </div>

        <select
          value={filterMarketplace}
          onChange={(e) => setFilterMarketplace(e.target.value)}
          className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 cursor-pointer"
        >
          {marketplaces.map((marketplace) => <option key={marketplace}>{marketplace}</option>)}
        </select>

        <div className="flex rounded-xl bg-gray-100 p-1">
          {([
            { value: "todos", label: "Todos" },
            { value: "habilitados", label: "Habilitados" },
            { value: "desabilitados", label: "Desabilitados" },
          ] as const).map((item) => (
            <button
              key={item.value}
              onClick={() => setStatusFilter(item.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                statusFilter === item.value ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {layout === "cards" ? (
        filteredProducts.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-gray-200 bg-white py-16 text-center text-gray-400 text-sm">
            <i className={`${icon} text-3xl block mb-2`}></i>
            {emptyLabel}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 items-start">
            {filteredProducts.map((product) => (
              <div key={product.id} className="flex flex-col gap-3">
                <OfferCard
                  item={{
                    slug: product.slug,
                    image: product.image,
                    name: product.name,
                    price: product.price,
                    originalPrice: product.originalPrice,
                    discount: product.discount,
                    marketplace: product.marketplace,
                    freeShipping: product.freeShipping,
                    fulfillment: product.fulfillment,
                    officialStoreName: product.officialStoreName || (product.officialStore ? "Loja Oficial" : ""),
                    affiliateLink: product.affiliateLink,
                    isFlash: product.badge === "Oferta Relâmpago",
                  }}
                  className="w-full"
                />
                <div className="rounded-2xl border border-gray-100 bg-white p-4">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${MARKETPLACE_COLORS[product.marketplace] || "bg-gray-100 text-gray-600"}`}>
                      {product.marketplace}
                    </span>
                    <span className="text-xs text-gray-500">{product.category}</span>
                  </div>
                  <label className="inline-flex items-center gap-3 cursor-pointer mb-3">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={product[flag]}
                        onChange={(e) => togglePlacement(product.id, e.target.checked)}
                        className="sr-only"
                      />
                      <div className={`w-11 h-6 rounded-full transition-colors ${product[flag] ? "bg-orange-500" : "bg-gray-200"}`}></div>
                      <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${product[flag] ? "translate-x-5" : ""}`}></div>
                    </div>
                    <span className={`text-sm font-semibold ${product[flag] ? "text-orange-600" : "text-gray-500"}`}>
                      {product[flag] ? "Habilitado" : "Desabilitado"}
                    </span>
                  </label>
                  <button
                    onClick={() => navigate(`/admin/produtos?edit=${product.id}`)}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-orange-50 hover:text-orange-500 hover:border-orange-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-edit-line mr-1"></i>Editar cadastro
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Produto</th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Categoria</th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Marketplace</th>
                <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="text-right px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center py-12 text-gray-400 text-sm">
                    <i className={`${icon} text-3xl block mb-2`}></i>
                    {emptyLabel}
                  </td>
                </tr>
              )}

              {filteredProducts.map((product) => (
                <tr key={product.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-10 h-10 rounded-lg object-cover flex-shrink-0 bg-gray-100"
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate max-w-xs">{product.name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">R$ {product.price.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-xs text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full font-medium">{product.category}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${MARKETPLACE_COLORS[product.marketplace] || "bg-gray-100 text-gray-600"}`}>
                      {product.marketplace}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <label className="inline-flex items-center gap-3 cursor-pointer">
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={product[flag]}
                          onChange={(e) => togglePlacement(product.id, e.target.checked)}
                          className="sr-only"
                        />
                        <div className={`w-11 h-6 rounded-full transition-colors ${product[flag] ? "bg-orange-500" : "bg-gray-200"}`}></div>
                        <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${product[flag] ? "translate-x-5" : ""}`}></div>
                      </div>
                      <span className={`text-sm font-semibold ${product[flag] ? "text-orange-600" : "text-gray-500"}`}>
                        {product[flag] ? "Habilitado" : "Desabilitado"}
                      </span>
                    </label>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => navigate(`/admin/produtos?edit=${product.id}`)}
                        className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-orange-50 hover:text-orange-500 hover:border-orange-200 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-edit-line mr-1"></i>Editar cadastro
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 bg-gray-900 text-white text-sm font-medium px-5 py-3 rounded-xl z-50">
          <i className="ri-checkbox-circle-line text-green-400 mr-2"></i>
          {toast}
        </div>
      )}
    </div>
  );
}
